import { get, without, isNil, capitalize, head } from 'lodash-es';
import { MPGE_ENGINES } from 'site-modules/shared/constants/mpg';
import { getSeoDataFromFeatures } from 'site-modules/shared/utils/seo-helper';
import { transformVehicleHistory } from 'site-modules/shared/utils/inventory/srp-card-utils';
import { ALLOWED_HISTORY_PROVIDERS } from 'site-modules/shared/constants/inventory/vehicle-history';
import { SCHEMA_URL } from 'site-modules/shared/pages/seo';
import { showTrimName } from 'client/site-modules/shared/utils/show-trim-name';
import { isPreProd } from 'site-modules/shared/utils/publication-states';
import { isUsed } from 'site-modules/shared/utils/inventory-utils/is-used';
import { getUsurpImages } from 'client/site-modules/shared/utils/inventory/srp-images';

import { getVehicleEngine } from './vehicle';
import { getOfferSchema } from './offer';
import { getAggregateRatingFromCoreConsumerReviews } from './review';

function getSeoObjectImage(seoObject) {
  const image = get(seoObject, 'image.url') || get(seoObject, 'image');

  return typeof image === 'string' ? image : '';
}

function getSchemaImages({ images, heroImage, seoObject }) {
  const validImages = images?.filter(imageData => get(imageData, 'image'));

  if (validImages?.length) {
    return validImages.map(({ image, thumb }) => ({
      '@type': 'ImageObject',
      url: image,
      height: 1067,
      width: 1600,
      thumbnail: {
        '@type': 'ImageObject',
        name: thumb,
      },
    }));
  }

  return getSeoObjectImage(seoObject) || heroImage;
}

/**
 * Get vehicle interior color
 * @param vinData
 * @return {Object} interior color
 */
export function getVehicleInteriorColor(vinData) {
  const vehicleInteriorColor = get(vinData, 'vehicleInfo.vehicleColors.interior.name', '');
  return vehicleInteriorColor ? { vehicleInteriorColor } : null;
}

/**
 * Build description without empty values
 * @param vinData
 * @return {String} Description
 */
export function getCarSchemaDescription(vinData) {
  const { cylinders, doors } = get(vinData, 'vehicleInfo.partsInfo', {});
  const description = [
    get(vinData, 'vehicleInfo.partsInfo.transmission', null),
    get(vinData, 'vehicleInfo.partsInfo.driveTrain', null),
    !isNil(cylinders) ? `${cylinders} cylinders` : null,
    get(vinData, 'vehicleInfo.styleInfo.bodyType', null),
    !isNil(doors) ? `${doors} doors` : null,
  ];
  return without(description, null).join(', ');
}

/**
 * Get vehicle mpg fuel efficiency schema
 *
 * @param mpgStyle
 * @return {Object}
 */
export function getFuelEfficiencyCarSchema(mpgStyle) {
  if (!mpgStyle) {
    return {};
  }

  const isMpge = MPGE_ENGINES.includes(get(mpgStyle, 'engineType', ''));
  const mpgData = isMpge ? mpgStyle.mpgeData : mpgStyle.mpgData;

  return {
    fuelEfficiency: {
      '@type': 'QuantitativeValue',
      unitText: isMpge ? 'mpge' : 'mpg',
      minValue: get(mpgData, 'highway'),
      maxValue: get(mpgData, 'city'),
      value: get(mpgData, 'combined'),
    },
  };
}

/**
 * Get vehicle EngineSpecification schema
 *
 * @param {Object} engineData
 * @return {Object}
 */
export function getVehicleEngineSchema(engineData) {
  if (!engineData) return {};

  const { engineVolume, engineType, horsepower } = engineData;

  return {
    vehicleEngine: {
      '@type': 'EngineSpecification',
      engineType,
      enginePower: {
        '@type': 'QuantitativeValue',
        unitText: 'hp',
        value: horsepower,
      },
      engineDisplacement: {
        '@type': 'QuantitativeValue',
        unitText: 'LTR',
        value: engineVolume,
      },
    },
  };
}

/**
 * Testing tool: https://search.google.com/structured-data/testing-tool
 * Schema: http://schema.org/Car
 *
 * @param {Object} vinData Inventory data
 * @param {Boolean} disableInventoryMLPhotoSort
 * @return {Object} seo vehicle description
 */
export function getCarVinSchema({ vinData, disableInventoryMLPhotoSort, city = '', stateName = '', urlPattern = '' }) {
  const { stockNumber, vin } = vinData;
  const { make, model, year, style, trim = '' } = get(vinData, 'vehicleInfo.styleInfo', {});
  const { transmission = '', driveTrain = '' } = get(vinData, 'vehicleInfo.partsInfo', {});
  const mileage = get(vinData, 'vehicleInfo.mileage', '');
  const itemCondition = isUsed(vinData.type) ? `${SCHEMA_URL}/UsedCondition` : `${SCHEMA_URL}/NewCondition`;

  const historyInfo = get(vinData, 'historyInfo', {});
  const { ownerText, usageType, historyProvider } = historyInfo;
  const { accidentText } = transformVehicleHistory(historyInfo);
  const vehicleHistory = !ALLOWED_HISTORY_PROVIDERS.includes(historyProvider)
    ? {}
    : {
        knownVehicleDamages: accidentText,
        numberOfPreviousOwners: ownerText,
        vehicleSpecialUsage: usageType,
      };

  const { photoUrls } = getUsurpImages(vinData, disableInventoryMLPhotoSort);
  const image = head(photoUrls);

  const vehicleEngine = getVehicleEngine(vinData);
  const bodyType = get(vinData, 'vehicleInfo.styleInfo.bodyType');

  const epaCombinedMPG = get(vinData, 'vehicleInfo.styleInfo.fuel.epaCombinedMPG');
  const numberOfSeats = get(vinData, 'vehicleInfo.styleInfo.numberOfSeats');

  const schema = {
    '@type': 'Vehicle',
    brand: {
      '@type': 'Brand',
      name: make,
    },
    model,
    itemCondition,
    manufacturer: make,
    productionDate: `${year}`,
    vehicleModelDate: `${year}`,
    name: `${year} ${make} ${model} ${showTrimName(make, trim) ? trim : ''}`,
    description: getCarSchemaDescription(vinData),
    image,
    sku: stockNumber,
    driveWheelConfiguration: driveTrain,
    vehicleConfiguration: style,
    vehicleTransmission: transmission,
    vehicleIdentificationNumber: vin,
    color: `${get(vinData, 'vehicleInfo.vehicleColors.exterior.name', '')}`,
    mileageFromOdometer: mileage
      ? {
          '@type': 'QuantitativeValue',
          value: mileage,
          unitCode: 'SMI',
        }
      : undefined,
    ...getVehicleInteriorColor(vinData),
    ...vehicleHistory,
    offers: getOfferSchema({
      vehicle: vinData,
      urlCity: city,
      urlStateName: stateName,
      urlPattern,
    }),
    ...(vehicleEngine && { vehicleEngine }),
    ...(epaCombinedMPG && {
      fuelEfficiency: {
        '@type': 'QuantitativeValue',
        unitCode: 'MPG',
        value: epaCombinedMPG,
      },
    }),
    ...(numberOfSeats && { seatingCapacity: numberOfSeats }),
  };
  if (bodyType) {
    schema.bodyType = bodyType;
  }
  return schema;
}

/**
 * Testing tool: https://search.google.com/structured-data/testing-tool
 * Schema: http://schema.org/Car
 */
export function getEditorialReviewCarSchema({
  makeModelSubmodelYear,
  seoObject,
  priceRange = [0],
  whatsNewContent,
  url,
  images,
  heroImage,
  isCorePage = false,
}) {
  const make = get(makeModelSubmodelYear, 'make.name', '');
  const model = get(makeModelSubmodelYear, 'model.name', '');
  const year = get(makeModelSubmodelYear, 'year', '');
  const isPreprod = isPreProd(get(makeModelSubmodelYear, 'pubStates', ''));

  const minPrice = Math.min(...priceRange);
  const maxPrice = Math.max(...priceRange);

  return {
    '@type': ['Vehicle', 'Product'],
    brand: make,
    model,
    manufacturer: make,
    productionDate: year,
    name: `${year} ${make} ${model}`,
    description: get(seoObject, 'description', ''),
    ...(isCorePage ? { url } : {}),
    ...((minPrice || maxPrice) &&
      !isPreprod && {
        offers: {
          '@type': 'Offer',
          price: minPrice || maxPrice,
          priceCurrency: 'USD',
          priceSpecification: {
            '@type': 'priceSpecification',
            minPrice,
            maxPrice,
            price: minPrice || maxPrice,
            priceCurrency: 'USD',
          },
        },
      }),
    image: getSchemaImages({ images, heroImage, seoObject }),
    ...(whatsNewContent?.length
      ? {
          additionalProperty: whatsNewContent.map(item => ({
            '@type': 'PropertyValue',
            name: "What's New",
            value: item,
          })),
        }
      : {}),
  };
}

/**
 * Testing tool: https://search.google.com/structured-data/testing-tool
 *
 * Schema: http://schema.org/Car
 */
export function getMpgPageCarSchema({ makeModelSubmodelYear, mpgStyle, consumerReviewsCount, seoObject }) {
  const aggregateRating = getAggregateRatingFromCoreConsumerReviews(consumerReviewsCount);

  return aggregateRating
    ? {
        ...getEditorialReviewCarSchema({ makeModelSubmodelYear, seoObject }),
        ...getFuelEfficiencyCarSchema(mpgStyle),
        aggregateRating,
      }
    : undefined;
}

/**
 * Testing tool: https://search.google.com/structured-data/testing-tool
 * Schema: http://schema.org/Car
 *
 * @return {Object} seo vehicle description
 */
export function getCorePageCarSchema({
  makeModelSubmodelYear,
  consumerReviews,
  seoObject,
  priceRange = [0],
  whatsNewContent,
  url,
  images,
  heroImage,
  isCorePage = false,
}) {
  const aggregateRating = getAggregateRatingFromCoreConsumerReviews(consumerReviews);

  return isCorePage || aggregateRating
    ? {
        ...getEditorialReviewCarSchema({
          makeModelSubmodelYear,
          seoObject,
          priceRange,
          whatsNewContent,
          url,
          images,
          heroImage,
          isCorePage,
        }),
        aggregateRating,
      }
    : undefined;
}

export function getVehicleMeasurementsSchemas(measurementsData) {
  const { width, height, weight } = measurementsData;

  return {
    width: {
      '@type': 'QuantitativeValue',
      unitText: 'in',
      value: width,
    },
    height: {
      '@type': 'QuantitativeValue',
      unitText: 'in',
      value: height,
    },
    weight: {
      '@type': 'QuantitativeValue',
      unitText: 'lbs',
      value: weight,
    },
  };
}

/**
 * Testing tool: https://search.google.com/structured-data/testing-tool
 * Schema: http://schema.org/Car
 *
 * @return {Object} seo vehicle description
 */
export function getExtendedCarSchema({
  makeModelSubmodelYear,
  style,
  consumerReviews,
  seoObject,
  priceRange = [0],
  whatsNewContent,
  url,
  images,
  heroImage,
  isCorePage = false,
}) {
  const features = get(style, 'features');

  if (!features) {
    return getCorePageCarSchema({
      makeModelSubmodelYear,
      consumerReviews,
      seoObject,
      priceRange,
      whatsNewContent,
      url,
      images,
      heroImage,
      isCorePage,
    });
  }

  const { generalData, mpg, engineData, measurementsData } = getSeoDataFromFeatures(features);
  const submodel = get(makeModelSubmodelYear, 'submodels.slug', '');
  const bodyType = submodel === 'suv' ? 'SUV' : capitalize(submodel);

  return {
    ...getCorePageCarSchema({
      makeModelSubmodelYear,
      consumerReviews,
      seoObject,
      priceRange,
      whatsNewContent,
      url,
      images,
      heroImage,
      isCorePage,
    }),
    bodyType,
    vehicleConfiguration: get(style, 'name', ''),
    seatingCapacity: get(style, 'numberOfSeats') || get(style, 'totalSeating'),
    ...generalData,
    ...getVehicleEngineSchema(engineData),
    ...getFuelEfficiencyCarSchema(mpg),
    ...getVehicleMeasurementsSchemas(measurementsData),
  };
}
